/**
 * An HSV colour picker: a saturation/brightness square, a hue slider, hex and
 * RGB fields, and the screen eyedropper where the browser has one.
 *
 * Built here rather than taken from a library because it is small and every
 * part maps onto something the platform already does: the hue slider is a
 * native range input (keyboard, screen reader and touch for free), the square
 * is pointer events with arrow-key support, and the eyedropper is the browser's
 * own EyeDropper API - offered only where it exists (Chromium), absent
 * elsewhere rather than broken.
 *
 * Controlled by a `#rrggbb` string. Hue is kept in local state as well, because
 * a grey or black colour has no hue of its own and dragging the square through
 * one would otherwise snap the slider back to red.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { clamp, hexToRgb, hsvToHex, rgbToHex, rgbToHsv, type Hsv } from '../color.ts';

/** The EyeDropper API, where the browser has it. */
interface EyeDropperLike {
  open(): Promise<{ sRGBHex: string }>;
}
const EyeDropperCtor = (globalThis as { EyeDropper?: new () => EyeDropperLike }).EyeDropper;

const HEX6 = /^#[0-9a-f]{6}$/i;

export function ColorPicker({
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const [hsv, setHsv] = useState<Hsv>(() => rgbToHsv(...hexToRgb(value)));
  const [hexText, setHexText] = useState(value);
  const area = useRef<HTMLDivElement>(null);

  // Follow the value from outside (a preset, a reset) - but keep our own hue
  // when the incoming colour is one we just produced, or a grey that has none.
  useEffect(() => {
    setHexText(value);
    if (hsvToHex(hsv).toLowerCase() === value.toLowerCase()) return;
    const next = rgbToHsv(...hexToRgb(value));
    setHsv(next.s === 0 || next.v === 0 ? { ...next, h: hsv.h } : next);
  }, [value]);

  const commit = (next: Hsv) => {
    setHsv(next);
    const hex = hsvToHex(next);
    setHexText(hex);
    onChange(hex);
  };

  function fromPointer(event: PointerEvent<HTMLDivElement>) {
    const box = area.current!.getBoundingClientRect();
    commit({
      h: hsv.h,
      s: clamp((event.clientX - box.left) / box.width),
      v: clamp(1 - (event.clientY - box.top) / box.height),
    });
  }

  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 0.1 : 0.02;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    commit({ h: hsv.h, s: clamp(hsv.s + move[0]), v: clamp(hsv.v + move[1]) });
  }

  const [r, g, b] = hexToRgb(hsvToHex(hsv));
  const setChannel = (index: 0 | 1 | 2, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const rgb: [number, number, number] = [r, g, b];
    rgb[index] = clamp(Math.round(n), 0, 255);
    const hex = rgbToHex(...rgb);
    setHsv(rgbToHsv(...rgb));
    setHexText(hex);
    onChange(hex);
  };

  async function pickFromScreen() {
    if (!EyeDropperCtor) return;
    try {
      const { sRGBHex } = await new EyeDropperCtor().open();
      if (HEX6.test(sRGBHex)) {
        const hex = sRGBHex.toLowerCase();
        setHsv(rgbToHsv(...hexToRgb(hex)));
        setHexText(hex);
        onChange(hex);
      }
    } catch {
      // Escape closes the eyedropper; nothing was picked.
    }
  }

  return (
    <div className={`cpick${disabled ? ' cpick--disabled' : ''}`}>
      <div
        ref={area}
        className="cpick__area"
        style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="彩度と明るさ"
        aria-valuetext={`彩度 ${Math.round(hsv.s * 100)}%、明るさ ${Math.round(hsv.v * 100)}%`}
        onPointerDown={(event) => {
          if (disabled) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          fromPointer(event);
        }}
        onPointerMove={(event) => {
          if (!disabled && event.currentTarget.hasPointerCapture(event.pointerId)) fromPointer(event);
        }}
        onKeyDown={disabled ? undefined : onKey}
      >
        <span
          className="cpick__thumb"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            backgroundColor: hsvToHex(hsv),
          }}
        />
      </div>

      <div className="cpick__row">
        <span className="cpick__swatch" style={{ backgroundColor: hsvToHex(hsv) }} />
        {EyeDropperCtor && (
          <button
            type="button"
            className="cpick__dropper"
            title="画面から色を取る"
            aria-label="画面から色を取る"
            disabled={disabled}
            onClick={() => void pickFromScreen()}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path
                d="M17.7 3.3a2.4 2.4 0 0 1 3.4 3.4l-2.5 2.5 1 1-1.4 1.4-1-1-7.8 7.8H6v-3.4l7.8-7.8-1-1 1.4-1.4 1 1zM8 16v1h1l7.4-7.4-1-1z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        <input
          className="cpick__hue"
          type="range"
          min={0}
          max={359}
          value={Math.round(hsv.h)}
          disabled={disabled}
          aria-label="色相"
          onChange={(event) => commit({ ...hsv, h: Number(event.target.value) })}
        />
      </div>

      <div className="cpick__fields">
        <div className="cpick__field cpick__field--hex">
          <label htmlFor={`${idPrefix}-hex`}>16進数</label>
          <input
            id={`${idPrefix}-hex`}
            value={hexText}
            maxLength={7}
            spellCheck={false}
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value.trim();
              setHexText(raw);
              const hex = raw.startsWith('#') ? raw : `#${raw}`;
              if (HEX6.test(hex)) {
                setHsv(rgbToHsv(...hexToRgb(hex)));
                onChange(hex.toLowerCase());
              }
            }}
            onBlur={() => setHexText(hsvToHex(hsv))}
          />
        </div>
        {(['R', 'G', 'B'] as const).map((channel, index) => (
          <div key={channel} className="cpick__field">
            <label htmlFor={`${idPrefix}-${channel}`}>{channel}</label>
            <input
              id={`${idPrefix}-${channel}`}
              type="number"
              min={0}
              max={255}
              value={[r, g, b][index]}
              disabled={disabled}
              onChange={(event) => setChannel(index as 0 | 1 | 2, event.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
